// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Hook for room create/join/conflict management.
 *
 * Extracted from RoomList to reduce component complexity.
 * Handles session conflict detection and resolution.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { triggerActiveSessionRefresh } from './useActiveSession';
import { api } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';
import type { ActiveSession } from '../types/game';

interface PendingAction {
  type: 'create' | 'join';
  gameId?: string;
  sessionId?: string;
}

interface UseRoomManagementOptions {
  selectedGame: string;
  displayName: string;
  sessionIdInput: string;
  onRoomJoined?: () => void;
}

export function useRoomManagement({
  selectedGame,
  displayName,
  sessionIdInput,
  onRoomJoined,
}: UseRoomManagementOptions) {
  const navigate = useNavigate();
  const { setSession, updateGameState } = useGameStore();
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => !!s.accessToken && !!s.user);

  const [submitting, setSubmitting] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  // Session conflict state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [existingSession, setExistingSession] = useState<ActiveSession | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [isLeavingSession, setIsLeavingSession] = useState(false);

  /** Convert display format to URL format for magic words */
  const formatMagicWordsForUrl = (input: string): string => {
    if (input.includes('-') && !input.includes(' ')) return input;
    return input
      .split(',')
      .map(part => part.trim().replace(/\s+/g, '-'))
      .join('-');
  };

  const handleCreateRoom = async (e: React.FormEvent, gameId?: string) => {
    e.preventDefault();
    const nameToUse = isAuthenticated && user ? user.username : displayName.trim();
    if (!nameToUse) return;

    setSubmitting(true);
    try {
      const response = await api.createRoom({
        host_display_name: nameToUse,
        game_id: gameId || selectedGame || undefined,
      });
      setSession(
        response.session_id,
        response.room_code,
        response.player_id,
        response.player_token,
        nameToUse
      );
      updateGameState({
        room_status: 'lobby',
        game_id: response.game_id,
        current_players: response.players,
        your_player_id: response.player_id,
        is_host: true,
        player_action_statuses: {},
      });
      onRoomJoined?.();
      navigate(`/lobby/${response.session_id}`);
    } catch (err) {
      const isSessionConflict =
        err instanceof Error &&
        (err.message.includes('SESSION_CONFLICT') ||
          err.message.includes('already in an active game'));
      if (isSessionConflict) {
        try {
          const mySession = await api.getMySession();
          if (mySession.active_session) {
            setExistingSession(mySession.active_session);
            setPendingAction({ type: 'create', gameId: gameId || selectedGame });
            setShowConflictModal(true);
            return;
          }
        } catch {
          // Fall through to error
        }
      }
      toast.error(err instanceof Error ? err.message : 'Failed to create room');
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinRoom = async (e: React.FormEvent, roomId?: string) => {
    e.preventDefault();
    const nameToUse = isAuthenticated && user ? user.username : displayName.trim();
    if (!nameToUse) return;

    const targetInput = roomId || sessionIdInput.trim();
    if (!targetInput) return;

    setSubmitting(true);
    try {
      let targetSessionId = targetInput;
      let roomCode = '';

      if (
        targetInput.includes(' ') ||
        targetInput.includes(',') ||
        targetInput.match(/^\d+-\w+-\d+-\w+-\d+-\w+$/)
      ) {
        const urlFormat = formatMagicWordsForUrl(targetInput);
        const lookupResponse = await api.lookupRoomByCode(urlFormat);
        targetSessionId = lookupResponse.session_id;
        roomCode = lookupResponse.room_code;
      }

      const response = await api.joinRoom({
        session_id: targetSessionId,
        display_name: nameToUse,
      });
      if (!roomCode) roomCode = response.room_code;

      setSession(
        response.session_id,
        roomCode,
        response.player_id,
        response.player_token,
        nameToUse
      );
      const isHost = response.current_players[0]?.player_id === response.player_id;
      updateGameState({
        room_status: 'lobby',
        game_id: selectedGame,
        current_players: response.current_players,
        your_player_id: response.player_id,
        is_host: isHost,
        player_action_statuses: {},
      });
      onRoomJoined?.();
      navigate(`/lobby/${response.session_id}`);
    } catch (err) {
      const isSessionConflict =
        err instanceof Error &&
        (err.message.includes('SESSION_CONFLICT') ||
          err.message.includes('already in an active game'));
      if (isSessionConflict) {
        try {
          const mySession = await api.getMySession();
          if (mySession.active_session) {
            setExistingSession(mySession.active_session);
            setPendingAction({ type: 'join', sessionId: targetInput });
            setShowConflictModal(true);
            return;
          }
        } catch {
          // Fall through to error
        }
      }
      setJoinError(err instanceof Error ? err.message : 'Failed to join room');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturnToExisting = () => {
    if (existingSession) navigate(`/lobby/${existingSession.session_id}`);
    setShowConflictModal(false);
    setExistingSession(null);
    setPendingAction(null);
  };

  const handleLeaveAndJoin = async () => {
    if (!existingSession || !pendingAction) return;
    setIsLeavingSession(true);
    try {
      await api.leaveMySession();
      triggerActiveSessionRefresh();
      setShowConflictModal(false);
      const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
      if (pendingAction.type === 'create') {
        await handleCreateRoom(fakeEvent, pendingAction.gameId);
      } else if (pendingAction.type === 'join' && pendingAction.sessionId) {
        await handleJoinRoom(fakeEvent, pendingAction.sessionId);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to leave session');
    } finally {
      setIsLeavingSession(false);
      setExistingSession(null);
      setPendingAction(null);
    }
  };

  const clearConflict = () => {
    setShowConflictModal(false);
    setExistingSession(null);
    setPendingAction(null);
  };

  return {
    submitting,
    joinError,
    setJoinError,
    showConflictModal,
    existingSession,
    pendingAction,
    isLeavingSession,
    handleCreateRoom,
    handleJoinRoom,
    handleReturnToExisting,
    handleLeaveAndJoin,
    clearConflict,
  };
}
